import { useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { CALENDLY_URL, scrollToSection } from "@/lib/scroll";

const LINKS = [
  { label: "The Problem", href: "#problem" },
  { label: "Deliverables", href: "#deliverables" },
  { label: "7-Day Sprint", href: "#process" },
  { label: "Who It's For", href: "#who-its-for" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="glass border-b border-line/70">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-sm text-smoke transition-colors hover:text-ink"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-button"
              className="hidden items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-cream transition-all duration-300 hover:bg-terra sm:inline-flex"
            >
              Book a fit call
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              className="rounded-full border border-line bg-white/70 p-2.5 text-ink lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <motion.div style={{ scaleX: progress }} className="h-[2px] origin-left bg-terra" />
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            data-testid="nav-mobile-menu"
            className="glass mx-4 mt-2 rounded-2xl border border-line p-4 lg:hidden"
          >
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                className="block border-b border-line/60 py-3 text-sm text-ink last:border-0"
              >
                {l.label}
              </a>
            ))}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-cta"
              className="mt-3 block rounded-full bg-terra py-3 text-center text-sm font-medium text-white"
            >
              Book a 15-minute fit call
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
